import React, {FC, useState, useEffect} from "react";
import styles from "./CaseSubmissionDetails.module.scss";
import {CustomBlockParamsType} from "@common/typing";
import * as dayjs from "dayjs";
import _ from "lodash";
import {AlertComponent} from "../alert/AlertComponent";
import {Spinner} from "..";
import {RelocationService} from "@common/services";
import {useSelector} from "react-redux";
import {RootState} from "../../store/store";

const DATE_FORMAT = "YYYY-MM-DD";

interface CaseSubmissionDetailsProps {
  submittedBy: string;
  reason: string;
  notes: string;
  hasCustomBlock: boolean;
  customBlockStartDate: string;
  customBlockEndDate: string;
  customBlockExtendedDate: string;
}

export const CaseSubmissionDetails: FC<CaseSubmissionDetailsProps> = ({
  submittedBy,
  reason,
  notes,
  hasCustomBlock,
  customBlockStartDate,
  customBlockEndDate,
  customBlockExtendedDate,
}) => {
  const relocationRequest = useSelector((state: RootState) => state.caseProfile.relocationRequest) as any;
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const [blockResGrid, setBlockResGrid] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const relocationService = RelocationService.getInstance();

  const validateSaveCustomBlock = (): boolean => hasCustomBlock !== blockResGrid;

  /**
   * Saves custom block based on Relocation Request endDate/extendedEndDate
   */
  const handleSaveBlock = async (): Promise<void> => {
    if (!validateSaveCustomBlock()) return;

    console.log(`Set Request (${relocationRequest.id}) Periods with Custom Block: ${blockResGrid}`);

    const endDate = dayjs(getCustomBlockEndDate());

    const customBlockPeriod = {
      start: relocationRequest.attributes.start_date,
      end: endDate.format(DATE_FORMAT),
      unit_id: relocationRequest.attributes.legacy_unit_id.toString(),
      action: blockResGrid ? "BLOCK" : "UNBLOCK",
      request_id: relocationRequest.id,
    } as CustomBlockParamsType;

    setShowSpinner(true);
    const apiResponse = await relocationService.setCustomBlockPeriod(customBlockPeriod);
    processBlockSet(apiResponse);
  };

  /**
   * Displays Alert to User based on the Response from Aailability Service
   * @param apiResponse
   */
  const processBlockSet = (apiResponse: any): void => {
    const attributes = apiResponse?.data?.attributes;
    const blocked = attributes?.blocked;
    const legacyTurnover = attributes?.legacy_turnover;
    const blockType = legacyTurnover ? "Legacy Turnover" : "Custom Block";
    if (!_.isNil(blocked) && blocked === blockResGrid) {
      const verboseStartDate = dayjs(attributes?.start).format(DATE_FORMAT);
      const verboseEndDate = dayjs(attributes?.end).format(DATE_FORMAT);

      setAlertMessage(`
        Success! ${blockType} Period updated. From ${verboseStartDate}
        to ${verboseEndDate}.`);

      setAlertType("success");
      setBlockResGrid(blocked);
      setShowAlert(true);
      console.log(`${blockType} Period Set for Request (${relocationRequest.id})`);
    } else {
      setAlertMessage(`Error! ${blockType} Period not updated.`);
      setAlertType("error");
      setShowAlert(true);
      setBlockResGrid(hasCustomBlock);
      console.log(`${blockType} Period not Set for Request (${relocationRequest.id})`);
    }
    setShowSpinner(false);
  };

  /**
   * Gets Enddate to be applied over Turnover
   */
  const getCustomBlockEndDate = (): string => {
    if (!_.isNil(customBlockExtendedDate) && customBlockExtendedDate !== "") {
      const endDate = dayjs(customBlockEndDate);
      const extendedEndDate = dayjs(customBlockExtendedDate);
      if (extendedEndDate.isAfter(endDate)) {
        return customBlockExtendedDate;
      }
    }
    return customBlockEndDate;
  };

  const handleBlockResGrid = (): void => {
    setBlockResGrid(!blockResGrid);
    setShowAlert(false);
  };

  useEffect(() => {
    setBlockResGrid(hasCustomBlock);
  }, [hasCustomBlock]);

  return (
    <div className={styles.caseSubmissionDetails}>
      <div className={styles.caseSubmissionDetailsTitle}>Submission Details</div>
      <div className={styles.caseSubmissionDetailsInfoGrid1x2}>
        <div>
          <div className={styles.caseSubmissionDetailsContentTitle}>Submitted by</div>
          <div className={styles.caseSubmissionDetailsContentText}>{submittedBy}</div>
        </div>
        <div>
          <div className={styles.caseSubmissionDetailsContentTitle}>Reason</div>
          <div className={styles.caseSubmissionDetailsContentText}>{reason}</div>
        </div>
      </div>
      <hr />

      <div className={styles.caseSubmissionDetailsInfoFlat}>
        <span className={styles.caseSubmissionDetailsContentTitle}>Notes</span>
        <div className={styles.caseSubmissionDetailsContentText}>{notes}</div>
      </div>
      <hr />

      <div className={styles.caseSubmissionDetailsInfoFlat}>
        <div className={styles.caseSubmissionDetailsSubTitle}>FM block on ResGrid</div>
        <div className={styles.caseSubmissionDetailsContentText}>
          The following has been pre-selected and should most often be set to "Yes". If the unit is perfectly fine and the FM was not due to the unit
          being uninhabitable, set to "No".
        </div>
      </div>

      <div className={styles.caseSubmissionDetailsInfoGrid1x3}>
        <div>
          <div className={styles.caseSubmissionDetailsContentTitle}>Start date</div>
          <div className={styles.caseSubmissionDetailsContentText}>{customBlockStartDate}</div>
        </div>
        <div>
          <div className={styles.caseSubmissionDetailsContentTitle}>Ending date</div>
          <div className={styles.caseSubmissionDetailsContentText}>{customBlockEndDate}</div>
        </div>
        <div>
          <div className={styles.caseSubmissionDetailsContentTitle}>Extended date</div>
          <div className={styles.caseSubmissionDetailsContentText}>{customBlockExtendedDate}</div>
        </div>
      </div>

      <div className={styles.caseSubmissionDetailsInfoFlat}>
        <span className={styles.caseSubmissionDetailsContentTitle}>Block ResGrid for dates shown above?</span>
        {showAlert && <AlertComponent text={<>{alertMessage}</>} className={alertType} iconSize="25" setShowAlert={setShowAlert} />}
        <div className={styles.caseSubmissionDetailsContentText}>
          {showSpinner ? (
            <Spinner />
          ) : (
            <div>
              <div className={styles.caseSubmissionDetailsInputContainer}>
                <input
                  type="radio"
                  name="blockResGrid"
                  className={styles.caseSubmissionDetailsInput}
                  checked={blockResGrid === true}
                  onChange={handleBlockResGrid}
                />
                <span className={styles.caseSubmissionDetailsContentText}>Yes</span>
              </div>
              <div className={styles.caseSubmissionDetailsInputContainer}>
                <input
                  type="radio"
                  name="blockResGrid"
                  className={styles.caseSubmissionDetailsInput}
                  checked={blockResGrid === false}
                  onChange={handleBlockResGrid}
                />
                <span className={styles.caseSubmissionDetailsContentText}>No</span>
              </div>
              <button className={validateSaveCustomBlock() ? styles.button : styles.buttonDisabled} onClick={handleSaveBlock}>
                Save
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
